import React, { Fragment, memo, MouseEvent, useMemo } from 'react';
import { AddIcon } from '../../../../assets/icons/AddIcon';
import { NotificationType } from '../../../../libraries/Notification';
import { CalendarLanguage } from '../../../../utils/CalendarUtils';
import { getMergedClassNames } from '../../../../utils/HTMLutils';
import { getTwoNumbersIfNotTen } from '../../../../utils/ParsingUtils';
import CalendarEvent, {
  CalendarEventProps,
} from '../../../calendarEvent/CalendarEvent';
import Notification from '../../../notification/Notification';
import CalendarChip from '../calendarChip/CalendarChip';
import './CalendarMonthDay.scss';
const rootClassName = 'calendar-month-day';

export interface CalendarMonthDayProps {
  day: Date;
  pocket?: boolean;
  isToday?: boolean;
  disabled?: boolean;
  disabledPrevAddButton?: boolean;
  language: CalendarLanguage;
  eventos?: CalendarEventProps[];
  showAddButton?: boolean;
  onAddButtonClick?: (
    day: Date,
    evt: MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => void;
  onPlusButtonClick?: (
    day: Date,
    evt: MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => void;
  onDateClick?: (
    day: Date,
    evt: MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => void;
  showPlusIcon?: boolean;
  highlightWeekends?: boolean;
}
function CalendarMonthDay(props: CalendarMonthDayProps) {
  const classNames = useMemo(() => {
    return getMergedClassNames([
      rootClassName,
      props.pocket ? 'pocket' : '',
      props.isToday ? 'today' : '',
      props.disabled ? 'disabled' : '',
      props.highlightWeekends &&
      (props.day.getDay() === 0 || props.day.getDay() === 6)
        ? 'highlight'
        : '',
    ]);
  }, [
    props.day,
    props.disabled,
    props.highlightWeekends,
    props.isToday,
    props.pocket,
  ]);

  const date = new Date();
  date.setDate(date.getDate() - 1);

  return (
    <div
      className={classNames}
      onClick={(evt) => {
        if (typeof props.onDateClick === 'function') {
          props.onDateClick?.(props.day, evt);
          evt.stopPropagation();
        }
      }}
    >
      <div className={`${rootClassName}-label-line`}>
        <div
          className={`${rootClassName}-day nunito`}
          onClick={(evt) => {
            if (
              props.pocket &&
              !props.disabled &&
              typeof props.onPlusButtonClick === 'function'
            ) {
              props.onPlusButtonClick(props.day, evt);
            }
          }}
        >
          <Notification
            count={props.pocket ? props.eventos?.length : 0}
            type={
              props.disabled
                ? NotificationType.Disabled
                : NotificationType.Primary
            }
            verticalOuter={11}
            horizontalOuter={14}
          >
            {getTwoNumbersIfNotTen(props.day.getDate())}
          </Notification>
        </div>
        <div>
          {!props.pocket &&
          props.showPlusIcon &&
          Boolean(props.eventos) &&
          (props.eventos?.length ?? 0) > 2 ? (
            <CalendarChip
              num={(props.eventos?.length ?? 0) - 2}
              onClick={(evt) => {
                if (typeof props.onPlusButtonClick === 'function') {
                  props.onPlusButtonClick(props.day, evt);
                  evt.stopPropagation();
                }
              }}
              disabled={props.disabled}
            />
          ) : undefined}
        </div>
      </div>
      {!props.pocket ? (
        <Fragment>
          <div className={`${rootClassName}-eventos`}>
            {props.eventos?.slice(0, 2)?.map((e) => {
              return (
                <CalendarEvent key={e.id} {...e} disabled={props.disabled} />
              );
            })}
          </div>
          {props.showAddButton ? (
            <button
              disabled={
                props.disabledPrevAddButton
                  ? props.day < date
                    ? true
                    : false
                  : false
              }
              className={`${rootClassName}-button`}
              onClick={(evt) => {
                if (typeof props.onAddButtonClick === 'function') {
                  props.onAddButtonClick?.(props.day, evt);
                  evt.stopPropagation();
                }
              }}
            >
              <span>
                <AddIcon />
                {props.language.addNew}
              </span>
            </button>
          ) : undefined}
        </Fragment>
      ) : undefined}
    </div>
  );
}

export default memo(CalendarMonthDay);
